<template>
  <div v-if="!isMspRoute">
    <div class="d-flex align-center">
      <v-icon class="mr-2" icon="$success" />
      <div class="subtitle1" v-html="$t(`modals.${SecurityGapAction.FIX_ALL}.fixDetails`)" />
    </div>
    <div
      class="body1 mt-4"
      v-html="$t(`securityGaps.securityGapTypes.${config.item.securityGapType}.description`)"
    />
  </div>
  <v-form v-model="valid" validate-on="lazy input" ref="form" v-else>
    <div class="warning-block d-flex pa-4 mb-4">
      <v-icon class="mr-2" icon="$warning"></v-icon>
      <span class="body2" v-html="$t(`modals.${SecurityGapAction.FIX_ALL}.description`)" />
    </div>
    <div class="info-block d-flex pa-4">
      <v-icon class="mr-2" icon="$success" />
      <div
        class="body2"
        v-html="
          `${$t(`modals.${SecurityGapAction.FIX_ALL}.fixDetails`)}: ${$t(`modals.${SecurityGapAction.FIX_ALL}.specificDescription.${config.item.securityGapType}`)}`
        "
      />
    </div>

    <div class="mt-6 mb-4 d-flex justify-space-between align-center">
      <div class="subtitle1">
        {{ $t("general.workspace") }}
      </div>
      <v-text-field
        density="compact"
        :placeholder="$t('general.search')"
        :aria-label="$t('general.search')"
        class="search-field"
        prepend-inner-icon="icon-search-xs"
        clearable
        clear-icon="icon-close-xs"
        hide-details
        @click.stop
        rounded
        max-width="280"
        @update:model-value="workspaceSearch = $event?.trim().toLowerCase()"
      />
    </div>
    <div class="body1 mb-4">
      {{ $t(`modals.${SecurityGapAction.FIX_ALL}.pleaseReviewTheListBelow`) }}
    </div>
    <table-wrapper>
      <v-data-table-server
        v-model="selectedItems"
        height="300"
        class="coronet-table--highlightable"
        :items="localValue.workspaces"
        :fixed-header="true"
        show-select
        item-key="workspaceId"
        hide-default-footer
        return-object
        :loading="loading"
        :items-length="totalWorkspaces"
      >
        <template #headers>
          <tr>
            <th class="checkbox-col">
              <bulk-selector-checkbox
                class="ml-2"
                :is-indeterminate="isIndeterminate"
                :all-items-on-page-selected="allItemsOnPageSelected"
                :items="localValue.workspaces"
                :all-items-selected="allItemsSelected"
                :show-menu="false"
                @selection-changed="onSelectionChanged"
              />
            </th>
            <template v-if="selectedItems.length === 0">
              <th>
                {{ $t(`modals.${SecurityGapAction.FIX_ALL}.workspaceName`) }}
              </th>
            </template>
            <template v-else>
              <th :colspan="8">
                <span>{{ selectedItemsQuantity }} {{ $t("general.selected") }}</span>
              </th>
            </template>
          </tr>
        </template>
        <template #item="{ item, index, isSelected, internalItem }">
          <tr :key="`${item.workspaceId}`">
            <td class="checkbox-col">
              <v-checkbox-btn
                :data-testid="`table-checkbox-row-${index}`"
                :model-value="isSelected([internalItem])"
                @update:modelValue="handleOneRowSelection($event, item)"
              />
            </td>
            <td>
              <span class="body2">{{ item.name }}</span>
            </td>
          </tr>
        </template>
      </v-data-table-server>
    </table-wrapper>
  </v-form>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, ref, watch } from "vue";
import { SecurityGapAction } from "@/constants/security-gaps.ts";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import { type SecurityGapItem, useSecurityGapsStore } from "@/_store/security-gaps.module.ts";
import TableWrapper from "@/components/TableWrapper.vue";
import BulkSelectorCheckbox from "@/components/BulkSelectorCheckbox.vue";
import { useSelectableTable } from "@/composables/useSelectableTable.ts";
import { storeToRefs } from "pinia";
import { useSelectorStore } from "@/_store/selector.module.ts";

import debounce from "lodash/debounce";
import cloneDeep from "lodash/cloneDeep";
import { isMspPortalRoute } from "@/_helpers/subscription";

export default defineComponent({
  components: { BulkSelectorCheckbox, TableWrapper },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<SecurityGapItem>>,
      required: true,
    },
  },
  emits: ["update:localValue", "update:valid"],
  setup(props, { emit }) {
    const loading = ref(false);
    const { selection } = storeToRefs(useSelectorStore());
    const { workspaces, totalWorkspaces } = storeToRefs(useSecurityGapsStore());
    const localValue = ref({
      workspaces: [] as { name: string; workspaceId: string }[],
    });
    const {
      selectedItems,
      handleOneRowSelection,
      onSelectionChanged,
      isIndeterminate,
      allItemsSelected,
      selectedItemsQuantity,
      allItemsOnPageSelected,
    } = useSelectableTable({
      totalItems: totalWorkspaces,
      items: workspaces,
    });
    const workspaceSearch = ref("");

    const isMspRoute = computed(() => isMspPortalRoute());

    const valid = computed(() => {
      return selectedItems.value.length > 0;
    });

    onMounted(async () => {
      if (isMspRoute.value) {
        loading.value = true;
        await useSecurityGapsStore().getSecurityGapWorkspaces(props.config.item.securityGapType);
        useSelectorStore().selectAllPages();
        localValue.value.workspaces = cloneDeep(workspaces.value);
        loading.value = false;
      }
    });

    watch(
      () => selectedItems.value,
      () => {
        if (isMspRoute.value) {
          emit("update:localValue", {
            action: SecurityGapAction.FIX_ALL,
            selection: selection.value,
            quantity: selectedItemsQuantity.value,
            securityGapType: props.config.item.securityGapType,
          });
        } else {
          emit("update:localValue", {
            action: SecurityGapAction.FIX_ALL,
            securityGapType: props.config.item.securityGapType,
            selection: {
              isAllPages: true,
              include: [],
              exclude: [],
            },
          });
        }
      },
      { deep: true, immediate: true }
    );

    watch(valid, (value) => {
      if (value === null) {
        return;
      }

      emit("update:valid", value);
    });

    watch(
      workspaceSearch,
      debounce((newVal) => {
        // TODO implement BE based search when BE will be ready
        loading.value = true;
        if (!newVal?.length) {
          localValue.value.workspaces = cloneDeep(workspaces.value);
        } else if (newVal.length > 2) {
          localValue.value.workspaces = workspaces.value.filter((v) =>
            v.name.toLowerCase().startsWith(newVal)
          );
        }
        loading.value = false;
      }, 500)
    );

    return {
      SecurityGapAction,
      workspaces,
      loading,
      selectedItems,
      handleOneRowSelection,
      onSelectionChanged,
      isIndeterminate,
      allItemsSelected,
      selectedItemsQuantity,
      allItemsOnPageSelected,
      selection,
      totalWorkspaces,
      isMspRoute,
      workspaceSearch,
      localValue,
      valid,
    };
  },
});
</script>

<style scoped lang="scss">
.v-table {
  border: 1px solid rgb(var(--v-theme-indigo-faint)) !important;
  box-shadow: none !important;
}

:deep(*) {
  .search-field {
    width: 280px;

    .v-input__control {
      width: 280px;
    }

    .v-field {
      background-color: rgb(var(--v-theme-indigo-faint)) !important;
    }

    .v-field__outline {
      display: none !important;
    }

    .icon-search-xs:before {
      color: rgb(var(--v-theme-indigo-medium));
    }
  }

  .icon-success-xs:before {
    color: rgb(var(--v-theme-indigo-base)) !important;
  }
}
</style>
